import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiOperationStatus } from '../../../../shared/data-model/models/api-operation-status.model';
import { ApiGatewayService } from '../../../../shared/services/api-gateway/api-gateway.service';
import { CommonUtils } from '../../../../shared/utils/common-utils';
import { OssOptional } from '../../../../shared/utils/oss-optional';
import { IVerificationWasSuccessful } from '../../../data-model/interfaces/i-verification-was-successful.interface';
import { ActivitySummaryFilter, getNulledOutActivitySummaryFilter } from '../../../data-model/models/activity-summary-filter.model';
import { CloseEventRequest } from '../../../data-model/models/close-event-request.model';
import { MissingActivityRecord } from '../../../data-model/models/missing-activity-record.model';
import { OssActivityRecord } from '../../../data-model/models/oss-activity-record.model';

interface BaseResponse {
  total_results: number;
  offset: number;
  limit: number;
}

type MissingResponse = BaseResponse & ApiOperationStatus & { results: MissingActivityRecord[] };

export interface ActivityResponse extends BaseResponse {
  results: OssActivityRecord[];
}

@Injectable({ providedIn: 'root' })
export class ActivityService {
  constructor(private readonly apiGateway: ApiGatewayService) {}

  getActivityRecords(
    serviceLocationId: number,
    limit: number = 1000,
    offset: number = 0,
    filter: ActivitySummaryFilter = getNulledOutActivitySummaryFilter(),
    isSupervisor: boolean = false
  ): Observable<ActivityResponse> {
    const urlSearchParams = this.buildUrlSearchParams(limit, offset, serviceLocationId, filter, false);
    if (!isSupervisor) {
      return this.apiGateway.get<ActivityResponse>('oss/activity/agent', null, true, null, urlSearchParams);
    }
    return this.apiGateway.get<ActivityResponse>('oss/activity/location', null, true, null, urlSearchParams);
  }

  getActivityRecordsCSV(
    serviceLocationId: number,
    limit: number = 1000,
    offset: number = 0,
    filter: ActivitySummaryFilter = getNulledOutActivitySummaryFilter(),
    isSupervisor: boolean = false
  ): Observable<Blob> {
    const urlSearchParams = this.buildUrlSearchParams(limit, offset, serviceLocationId, filter, true);
    if (!isSupervisor) {
      return this.apiGateway.get<Blob>('oss/activity/agent', null, true, { responseType: 'blob' }, urlSearchParams, '1.0.0', 'blob');
    }
    return this.apiGateway.get<Blob>('oss/activity/location', null, true, { responseType: 'blob' }, urlSearchParams, '1.0.0', 'blob');
  }

  getMissingItems(
    serviceLocationId: number,
    limit: number = 1000,
    offset: number = 0,
    filter: ActivitySummaryFilter = getNulledOutActivitySummaryFilter()
  ): Observable<ActivityResponse> {
    const urlSearchParams = this.buildUrlSearchParams(limit, offset, serviceLocationId, filter);
    return this.apiGateway
      .get<MissingResponse>('oss/activity/getMissingActivityLogRecords', null, true, null, urlSearchParams)
      .pipe(map(response => this.mapMissingActivityResponseToGetActivityResponse(response)));
  }

  getMissingItemsCSV(
    serviceLocationId: number,
    limit: number = 1000,
    offset: number = 0,
    filter: ActivitySummaryFilter = getNulledOutActivitySummaryFilter()
  ): Observable<Blob> {
    const urlSearchParams = this.buildUrlSearchParams(limit, offset, serviceLocationId, filter, true);
    return this.apiGateway.get<Blob>('oss/activity/getMissingActivityLogRecords', null, true, { responseType: 'blob' }, urlSearchParams, '1.0.0', 'blob');
  }

  patchActivityRecordItemNumber(activityRecordId: number, itemNumber: string): Observable<ApiOperationStatus & IVerificationWasSuccessful> {
    return this.apiGateway.patch<ApiOperationStatus & IVerificationWasSuccessful>(`oss/activity/index`, {
      activity_id: activityRecordId,
      item_num: itemNumber,
    });
  }

  addActivityRecord(request: CloseEventRequest): Observable<ApiOperationStatus & IVerificationWasSuccessful & { activity_id: number }> {
    return this.apiGateway.post<ApiOperationStatus & IVerificationWasSuccessful & { activity_id: number }>('oss/activity/index', { ...request });
  }

  getActivityRecord(activityId: number): Observable<OssActivityRecord> {
    return this.apiGateway.get<OssActivityRecord>('oss/activity/getActivity', { activity_id: activityId });
  }

  updateActivityRecord(request: CloseEventRequest): Observable<ApiOperationStatus & { data: OssActivityRecord }> {
    return this.apiGateway.patch<ApiOperationStatus & { data: OssActivityRecord }>('oss/activity/index', request);
  }

  deleteActivityRecord(activityId: number): Observable<ApiOperationStatus> {
    return this.apiGateway.delete<ApiOperationStatus>('oss/activity/index', { activity_id: activityId });
  }

  validateActivityLogRecord(activityId: number, isValidated: boolean, quickbaseData: Record<string, unknown> | null): Observable<OssActivityRecord> {
    const body = { activity_id: activityId, is_validated: isValidated };
    if (isValidated && quickbaseData) {
      body['quickbase_data'] = quickbaseData;
    }
    return this.apiGateway
      .patch<{ success: boolean; data: OssActivityRecord }>('oss/activity/validateActivityLogRecord', body)
      .pipe(map(response => response.data));
  }

  private mapP1ActivityRecordToOssActivityRecord(missingActivityRecord: MissingActivityRecord): OssActivityRecord {
    return {
      crash_num: null,
      created_at: null,
      deleted_at: null,
      disposition: null,
      has_report: null,
      id: null,
      intersecting_road: null,
      is_missing: null,
      item_num: missingActivityRecord.ItemNumber,
      location: missingActivityRecord.Location,
      modified_at: null,
      notes: null,
      p1_close_datetime: null,
      p1_create_datetime: null,
      p1_cross_street: null,
      p1_enroute_datetime: null,
      p1_incident_datetime: null,
      p1_onscene_datetime: null,
      primary_road: null,
      service_location_id: null,
      time_arrived: null,
      time_cleared: null,
      time_closed: null,
      time_dispatched: null,
      uiLocation: null,
      username: null,
      user_id: null,
      verification_status: null,
      verified_at: null,
      verified_by: null,
      report_status: null,
      signal: null,
      incident_datetime: null,
      crash_datetime: missingActivityRecord.Date ? new Date(missingActivityRecord.Date) : null,
      crash_date: null,
      crash_time: null,
      crash_datetime_source: null,
      ecrash_submission_successful: null,
      ecrash_submitted_by: null,
      ecrash_submission_datetime: null,
      quickbase_data: null,
    };
  }

  private mapMissingActivityResponseToGetActivityResponse(response: MissingResponse): ActivityResponse {
    return {
      total_results: response.total_results,
      results: response.results.map(result => this.mapP1ActivityRecordToOssActivityRecord(result)),
      offset: response.offset,
      limit: response.limit,
    };
  }

  private buildUrlSearchParams(
    limit: number,
    offset: number,
    serviceLocationId: number,
    filter: ActivitySummaryFilter,
    isCsvExport: boolean = false
  ): URLSearchParams {
    const urlSearchParams = new URLSearchParams();

    OssOptional.of(limit).ifPresent(value => urlSearchParams.append('limit', value.toString()));
    OssOptional.of(offset).ifPresent(value => urlSearchParams.append('offset', value.toString()));
    OssOptional.of(serviceLocationId).ifPresent(value => urlSearchParams.append('service_location_id', value.toString()));

    [
      { key: 'item_number', value: filter?.itemNumber },
      { key: 'agent', value: filter?.agent },
      { key: 'start_date', value: filter?.startDate },
      { key: 'end_date', value: filter?.endDate },
      { key: 'start_time', value: filter?.startTime },
      { key: 'end_time', value: filter?.endTime },
      { key: 'location', value: filter?.location },
    ]
      .filter(({ value }) => CommonUtils.isDefinedAndNonEmptyString(value))
      .forEach(({ key, value }) => urlSearchParams.append(key, value));

    (filter?.dispositions ?? []).forEach(status => urlSearchParams.append('disposition', status));
    (filter?.signals ?? []).forEach(signal => urlSearchParams.append('signal', signal));

    urlSearchParams.append('output_format', isCsvExport ? 'csv' : 'json');

    return urlSearchParams;
  }
}
